import { useState } from 'react';
import { Rocket, Check, Download, Zap } from 'lucide-react';

interface TokenCaptureProps {
  onTokenSubmit: (token: string) => void;
}

const CAPTURE_SCRIPT = `(function(){const _f=window.fetch;window.fetch=async function(...a){const r=await _f.apply(this,a);const h=a[1]?.headers;const auth=h?.Authorization||h?.authorization;if(auth?.startsWith('Bearer ')){const t=auth.slice(7);prompt('COPIA QUESTO TOKEN:',t);}return r;};alert('Ora cerca una citta (es. Milano)');})();`;

export function TokenCapture({ onTokenSubmit }: TokenCaptureProps) {
  const [token, setToken] = useState('');
  const [method, setMethod] = useState<'choose' | 'extension' | 'manual'>('choose');
  const [manualStep, setManualStep] = useState<1 | 2>(1);

  const handleManualStart = async () => {
    await navigator.clipboard.writeText(CAPTURE_SCRIPT);
    window.open('https://www.idealista.it', '_blank');
    setManualStep(2);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (token.trim()) {
      onTokenSubmit(token.trim());
    }
  };

  // Choose method screen
  if (method === 'choose') {
    return (
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md mx-auto">
        <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center">Collega Idealista</h2>
        <p className="text-gray-500 mb-6 text-center">Scegli come vuoi procedere</p>

        {/* Extension method - RECOMMENDED */}
        <button
          onClick={() => setMethod('extension')}
          className="w-full flex items-start gap-4 p-4 rounded-xl border-2 border-green-200 bg-green-50 hover:bg-green-100 transition-all mb-4 text-left"
        >
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
            <Zap className="w-6 h-6 text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-bold text-green-800">Estensione Chrome</span>
              <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">Consigliato</span>
            </div>
            <p className="text-sm text-green-700 mt-1">
              Installa una volta, cattura token con 1 click
            </p>
          </div>
        </button>

        {/* Manual method */}
        <button
          onClick={() => setMethod('manual')}
          className="w-full flex items-start gap-4 p-4 rounded-xl border-2 border-gray-200 hover:bg-gray-50 transition-all text-left"
        >
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gray-400 flex items-center justify-center">
            <Rocket className="w-6 h-6 text-white" />
          </div>
          <div>
            <span className="font-bold text-gray-800">Metodo Manuale</span>
            <p className="text-sm text-gray-600 mt-1">
              Copia/incolla nella console (5 passaggi)
            </p>
          </div>
        </button>
      </div>
    );
  }

  // Extension instructions
  if (method === 'extension') {
    return (
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md mx-auto">
        <button onClick={() => setMethod('choose')} className="text-gray-500 hover:text-gray-700 mb-4">
          ← Indietro
        </button>

        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Installa Estensione</h2>

        <div className="space-y-4 mb-6">
          <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold">1</span>
            <div>
              <p className="font-medium">Scarica l'estensione</p>
              <a
                href="/extension"
                download="homefinder-extension.zip"
                className="inline-flex items-center gap-2 mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all"
              >
                <Download className="w-4 h-4" />
                Scarica ZIP
              </a>
            </div>
          </div>

          <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold">2</span>
            <div>
              <p className="font-medium">Apri Chrome Extensions</p>
              <p className="text-sm text-gray-600">
                Vai a <code className="bg-gray-200 px-1 rounded">chrome://extensions</code>
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold">3</span>
            <div>
              <p className="font-medium">Attiva "Modalita sviluppatore"</p>
              <p className="text-sm text-gray-600">Toggle in alto a destra</p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold">4</span>
            <div>
              <p className="font-medium">Trascina la cartella estratta</p>
              <p className="text-sm text-gray-600">Oppure clicca "Carica estensione non pacchettizzata"</p>
            </div>
          </div>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-green-800">
            <strong>Fatto!</strong> Ora vai su Idealista, fai una ricerca, e clicca l'icona dell'estensione per copiare il token.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <input
            type="text"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Incolla il token qui..."
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 mb-3"
          />
          <button
            type="submit"
            disabled={!token.trim()}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-bold rounded-lg transition-all"
          >
            <Check className="w-5 h-5" />
            Collega
          </button>
        </form>
      </div>
    );
  }

  // Manual method
  return (
    <div className="bg-white rounded-xl shadow-lg p-8 max-w-md mx-auto text-center">
      <button onClick={() => setMethod('choose')} className="text-gray-500 hover:text-gray-700 mb-4 block">
        ← Indietro
      </button>

      <h2 className="text-2xl font-bold text-gray-900 mb-6">Metodo Manuale</h2>

      {manualStep === 1 ? (
        <>
          <button
            onClick={handleManualStart}
            className="w-full flex items-center justify-center gap-3 px-6 py-4 rounded-xl bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold text-lg transition-all shadow-lg hover:shadow-xl"
          >
            <Rocket className="w-6 h-6" />
            Inizia Setup
          </button>
          <p className="text-sm text-gray-500 mt-4">
            Si aprira Idealista. Poi segui le istruzioni.
          </p>
        </>
      ) : (
        <>
          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4 mb-6 text-left">
            <p className="font-bold text-yellow-800 mb-2">Su Idealista:</p>
            <ol className="text-sm text-yellow-700 space-y-1">
              <li><b>1.</b> Premi <kbd className="px-1.5 py-0.5 bg-yellow-200 rounded text-xs font-mono">F12</kbd></li>
              <li><b>2.</b> Clicca su <b>Console</b></li>
              <li><b>3.</b> Premi <kbd className="px-1.5 py-0.5 bg-yellow-200 rounded text-xs font-mono">Ctrl+V</kbd> e <kbd className="px-1.5 py-0.5 bg-yellow-200 rounded text-xs font-mono">Invio</kbd></li>
              <li><b>4.</b> Cerca una citta (es. "Milano")</li>
              <li><b>5.</b> Copia il token dal popup</li>
            </ol>
          </div>

          <form onSubmit={handleSubmit}>
            <input
              type="text"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Incolla il token qui..."
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 mb-3 text-center"
              autoFocus
            />
            <button
              type="submit"
              disabled={!token.trim()}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-bold rounded-lg transition-all"
            >
              <Check className="w-5 h-5" />
              Fatto!
            </button>
          </form>

          <button
            onClick={() => setManualStep(1)}
            className="mt-4 text-sm text-gray-500 hover:text-gray-700"
          >
            Ricomincia
          </button>
        </>
      )}
    </div>
  );
}
